import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Navigation, Search, X, Loader2 } from 'lucide-react';

/**
 * PlacesAutocompleteInput - Google Places Autocomplete component
 * @param {string} value - Current input value
 * @param {function} onChange - Callback when place is selected
 * @param {string} placeholder - Input placeholder
 * @param {string} type - 'pickup' or 'dropoff' for styling
 * @param {string} country - Country restriction (default: 'al' for Albania)
 */
const PlacesAutocompleteInput = ({ 
  value, 
  onChange, 
  placeholder = 'Kërko vendndodhjen...', 
  type = 'pickup',
  country = 'al',
  className = ''
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isReady, setIsReady] = useState(false);
  
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const debounceTimer = useRef(null);

  // Initialize Google Places services
  useEffect(() => {
    const initServices = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        placesService.current = new window.google.maps.places.PlacesService(document.createElement('div'));
        setIsReady(true);
      }
    };

    if (window.google && window.google.maps) {
      initServices();
    } else {
      // Wait for Google Maps to load
      const checkGoogle = setInterval(() => {
        if (window.google && window.google.maps) {
          initServices();
          clearInterval(checkGoogle);
        }
      }, 100);

      return () => clearInterval(checkGoogle);
    }
  }, []);

  // Sync with external value
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search for places with debounce
  const searchPlaces = useCallback((input) => {
    if (!input || input.length < 2 || !autocompleteService.current) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);

    autocompleteService.current.getPlacePredictions(
      {
        input,
        componentRestrictions: { country },
        types: ['geocode', 'establishment']
      },
      (predictions, status) => {
        setIsLoading(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
        }
      }
    );
  }, [country]);

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Debounce search
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      searchPlaces(newValue);
    }, 300);
  };

  // Handle place selection
  const handleSelectPlace = (prediction) => {
    if (!placesService.current) return;

    setIsLoading(true);
    
    placesService.current.getDetails(
      { 
        placeId: prediction.place_id, 
        fields: ['geometry', 'name', 'formatted_address'] 
      },
      (place, status) => {
        setIsLoading(false);
        
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const address = place.formatted_address || prediction.description;
          setInputValue(address);
          setShowSuggestions(false);
          setSuggestions([]);
          
          // Call onChange with full place data
          onChange({
            address,
            name: place.name,
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
            placeId: prediction.place_id
          });
        }
      }
    );
  };

  // Clear input
  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setShowSuggestions(false);
    onChange({ address: '', lat: null, lng: null });
    inputRef.current?.focus();
  };

  const icon = type === 'pickup' 
    ? <MapPin className="w-5 h-5 text-green-500" />
    : <Navigation className="w-5 h-5 text-red-500" />;

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2">
          {icon}
        </div>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          disabled={!isReady}
          className={`w-full pl-11 pr-10 py-3 border rounded-xl focus:ring-2 transition-all ${
            type === 'pickup' 
              ? 'border-green-200 focus:ring-green-500 focus:border-green-500' 
              : 'border-red-200 focus:ring-red-500 focus:border-red-500'
          } ${!isReady ? 'bg-gray-100' : 'bg-white'}`}
          data-testid={`places-input-${type}`}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {isLoading ? (
            <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
          ) : inputValue ? (
            <button 
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded-full"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          ) : (
            <Search className="w-5 h-5 text-gray-300" />
          )}
        </div>
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.place_id}
              type="button"
              onClick={() => handleSelectPlace(suggestion)}
              className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition ${
                index !== suggestions.length - 1 ? 'border-b border-gray-100' : ''
              }`}
              data-testid={`suggestion-${index}`}
            >
              <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">
                  {suggestion.structured_formatting?.main_text || suggestion.description.split(',')[0]}
                </p>
                <p className="text-sm text-gray-500 truncate">
                  {suggestion.structured_formatting?.secondary_text || suggestion.description}
                </p>
              </div>
            </button>
          ))}
          
          {/* Google Attribution */}
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
            <img 
              src="https://developers.google.com/static/maps/documentation/images/powered_by_google_on_white.png" 
              alt="Powered by Google" 
              className="h-4"
            />
          </div>
        </div>
      )}

      {/* Loading state when Google not ready */}
      {!isReady && (
        <p className="mt-1 text-xs text-gray-400">Duke ngarkuar hartat...</p>
      )}
    </div>
  );
};

export default PlacesAutocompleteInput;
